import { createElement, useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Groups } from "@mui/icons-material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { getData, postData } from "../../services/base.service";
import { useNotification } from "../Shared/NotificationProvider";
import EditableDataGrid from "../Shared/EditableDataGrid";
import ReferencePartnerActionsCell from "../references/ReferencePartnerActionsCell";
import { ProjectDetail, ProjectPartnerItem } from "../../types/projectTypes";
import { numberOrNull } from "./projectUtils";

type ProjectPartnersTabProps = {
    detail: ProjectDetail;
    isEditMode: boolean;
    setLoading: Function;
    refresh: () => Promise<void>;
};

// `id` is the partnerID (row key, used by ReferencePartnerActionsCell to link out), `linkID` is
// bridge_partners_projects.ID. Shares are shown/edited in percent and stored as fractions.
type PartnerRow = {
    id: number;
    linkID?: number;
    name: string;
    is_jv_leader: string;
    input_share: number | string | null;
    profit_share: number | string | null;
    comment: string;
};

const columns: GridColDef[] = [
    { field: 'name', headerName: 'Partner', flex: 1.5, editable: false },
    { field: 'is_jv_leader', headerName: 'JV leader', flex: 0.6, editable: true, type: 'singleSelect', valueOptions: ['Yes', 'No'],
        description: 'This partner leads the consortium (INT is partner).' },
    { field: 'input_share', headerName: 'Input share %', flex: 0.7, editable: true, type: 'number' },
    { field: 'profit_share', headerName: 'Profit share %', flex: 0.7, editable: true, type: 'number' },
    { field: 'comment', headerName: 'Comment', flex: 1.2, editable: true },
    {
        field: 'actions', headerName: 'Links', width: 80, editable: false, sortable: false, filterable: false,
        renderCell: (params: GridRenderCellParams) => createElement(ReferencePartnerActionsCell, { row: params.row }),
    },
];

const toPercent = (fraction: number | null) => (fraction === null ? null : Math.round(Number(fraction) * 10000) / 100);
const toFraction = (percent: number | string | null) => {
    const value = numberOrNull(percent);
    return value === null ? null : value / 100;
};

const toRow = (p: ProjectPartnerItem): PartnerRow => ({
    id: p.partnerID,
    linkID: p.id,
    name: p.name,
    is_jv_leader: p.is_jv_leader ? 'Yes' : 'No',
    input_share: toPercent(p.input_share_partner),
    profit_share: toPercent(p.profit_share_partner),
    comment: p.comment,
});

const payload = (row: PartnerRow) => ({
    is_jv_leader: row.is_jv_leader === 'Yes',
    input_share_partner: toFraction(row.input_share),
    profit_share_partner: toFraction(row.profit_share),
    comment: row.comment || null,
});

function ProjectPartnersTab({ detail, isEditMode, setLoading, refresh }: ProjectPartnersTabProps) {
    const notify = useNotification();
    const projectId = detail.project.ID;
    const [rows, setRows] = useState<PartnerRow[]>(detail.partners.map(toRow));

    useEffect(() => { setRows(detail.partners.map(toRow)); }, [detail.partners]);

    const handleChange = async (wrapper: { rows: PartnerRow[] }) => {
        const next = wrapper.rows || [];
        const additions = next.filter((row) => !rows.some((p) => p.id === row.id));
        const removals = rows.filter((p) => !next.some((row) => row.id === p.id));
        const updates = next.filter((row) => {
            const before = rows.find((p) => p.id === row.id);
            return before && before.linkID !== undefined && JSON.stringify(payload(before)) !== JSON.stringify(payload(row));
        });
        for (const row of [...additions, ...updates]) {
            const shares = [numberOrNull(row.input_share), numberOrNull(row.profit_share)];
            if (shares.some((s) => s !== null && (s < 0 || s > 100))) {
                notify('Shares must be between 0 and 100 %.', 'warning');
                return;
            }
        }

        setLoading(true);
        try {
            for (const row of removals) if (row.linkID !== undefined) await postData('projects/partners/remove', { id: row.linkID });
            for (const row of additions) await postData('projects/partners/add', { projectId, partnerID: row.id, ...payload(row) });
            for (const row of updates) await postData('projects/partners/update', { id: row.linkID, ...payload(row) });
            await refresh();
        } catch (error) {
            notify((error as Error).message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box paddingTop={2}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ marginBottom: 1 }}>
                <Groups /><Typography variant="h6">Consortium partners</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {detail.project.lead ? 'Integration leads this consortium.' : 'Integration is a partner in this consortium - mark the JV leader below.'}
            </Typography>
            <EditableDataGrid
                col={columns}
                rows={rows}
                editIsOn={isEditMode}
                formData={{ rows }}
                setFormData={handleChange}
                dataPath="rows"
                preselectionIsOn
                preselectionDisplayField="name"
                preselectionValueField="id"
                readOnlyFields={['name']}
                preselectionSearchFn={(query: string) => getData(`projects/${projectId}/available-partners?search=${encodeURIComponent(query)}`)}
                preselectionSearchLabel="Search for a partner by name or short name"
            />
        </Box>
    );
}

export default ProjectPartnersTab;
